// App.js - Marc Fisher Rebranded Version (with Streaming Audio)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import './App.css'; // The new CSS file will style this
import { FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import DualWaveformVisualizer from './components/DualWaveformVisualizer';
import logoFromFile from './assets/mf-logo.png';

const MarcFisherLogo = () => (
  <img 
    style={{ width: '200px', height: 'auto' }} 
    src={logoFromFile} 
    alt='Marc Fisher Logo'
  />
);


function App() {
  // State Variables
  const [isConnected, setIsConnected] = useState(false);
  const [status, setStatus] = useState('Disconnected');
  const [isListening, setIsListening] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [debugMode, setDebugMode] = useState(false);

  // Refs for various objects
  const audioContextRef = useRef(null);
  const socketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const userStreamRef = useRef(null);
  
  // Refs for streaming audio playback
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const currentAudioRef = useRef(null);

  // Function to play the next audio chunk in the queue
  const playNextInQueue = useCallback(() => {
    if (audioQueueRef.current.length > 0) {
      isPlayingRef.current = true;
      setIsAiSpeaking(true);
      
      const audioBlob = audioQueueRef.current.shift();
      const audioUrl = URL.createObjectURL(audioBlob);
      const newAudio = new Audio(audioUrl);
      currentAudioRef.current = newAudio;

      newAudio.play().catch(e => console.error("Error playing audio:", e));
      
      newAudio.onended = () => {
        playNextInQueue(); // Play the next one
      };
    } else {
      // Queue is empty, we're done
      isPlayingRef.current = false;
      setIsAiSpeaking(false);
      setStatus('Ready');
      currentAudioRef.current = null;
    }
  }, []);


  // Setup Socket Connection & Event Handlers
  useEffect(() => {
    const socketURL =
      process.env.NODE_ENV === 'production'
        ? undefined
        : process.env.REACT_APP_SERVER_URL || 'http://localhost:3001';

    socketRef.current = io(socketURL);

    socketRef.current.on('connect', () => {
      setIsConnected(true);
      setStatus('Connected');
    });

    socketRef.current.on('disconnect', () => {
      setIsConnected(false);
      setStatus('Disconnected');
      setIsListening(false);
      setIsAiSpeaking(false);
      isPlayingRef.current = false;
      audioQueueRef.current = [];
    });

    socketRef.current.on('transcription', (data) => {
      if (data?.text) {
        setConversation((prev) => [...prev, { role: 'user', text: data.text }]);
      }
    });

    // STREAMING FIX: Listen for the text part of the response
    socketRef.current.on('aiResponseText', (data) => {
        if (data?.text) {
            const cleanText = data.text.replace(/<[^>]*>|<\/[^>]*>/g, '');
            setConversation((prev) => [...prev, { role: 'assistant', text: cleanText }]);
            setStatus('Assistant is speaking...');
        }
    });

    // STREAMING FIX: Listen for audio chunks and queue them
    socketRef.current.on('audioChunk', (chunk) => {
        // The server sends a raw buffer, which we convert to a Blob
        audioQueueRef.current.push(new Blob([chunk], { type: 'audio/mpeg' }));
        if (!isPlayingRef.current) {
            playNextInQueue();
        }
    });
    
    // STREAMING FIX: The 'audioEnd' event signals the end of a response stream
    socketRef.current.on('audioEnd', () => {
        console.log("Server finished sending audio.");
        // The queue will handle setting the final status when it's empty
    });

    socketRef.current.on('error', (error) => {
      console.error('Socket error:', error);
      setIsListening(false);
      setIsAiSpeaking(false);
      isPlayingRef.current = false;
      setStatus('Error occurred');
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      if (userStreamRef.current) {
        userStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [playNextInQueue]);

  const startListening = async () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      userStreamRef.current = stream;

      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        await sendAudioToServer(audioBlob);
      };

      mediaRecorderRef.current.start(1000); // Send audio every second
      setIsListening(true);
      setStatus('Listening...');
    } catch (error) {
      console.error('Error starting recording:', error);
      setStatus('Error accessing microphone');
    }
  };

  const stopListening = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
      }
      setIsListening(false);
      setStatus('Processing...');
    }
  };

  const toggleListening = () => {
    isListening ? stopListening() : startListening();
  };

  const sendAudioToServer = async (audioBlob) => {
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Audio = reader.result.split(',')[1];
        socketRef.current.emit('audioChunk', { audioChunk: base64Audio });
      };
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      console.error('Error sending audio to server:', error);
    }
  };

  return (
    <div className="app-container">
      <header className="app-header">
        <MarcFisherLogo />
      </header>

      <main className="app-main">
        <div className="visualizer-container">
          <DualWaveformVisualizer
            userStream={userStreamRef.current}
            aiAudio={currentAudioRef.current} // Pass the currently playing audio element
            isUserSpeaking={isListening}
            isAiSpeaking={isAiSpeaking}
          />
        </div>

        <div className="controls-container">
          <div className="status-indicator">
            <div className={`status-dot ${
              isListening ? 'status-listening' : isAiSpeaking ? 'status-speaking' : isConnected ? 'status-connected' : 'status-disconnected'
            }`}></div>
            <span className="status-text">{status}</span>
          </div>

          <div className="action-area">
            <button
              onClick={toggleListening}
              disabled={!isConnected || isAiSpeaking}
              className={`mic-button ${isListening ? 'listening' : ''} ${(!isConnected || isAiSpeaking) ? 'disabled' : ''}`}
            >
              {isListening ? <FaMicrophoneSlash /> : <FaMicrophone />}
            </button>
            <div className="action-text">
              <p>
                {isListening
                  ? "Listening..."
                  : isAiSpeaking
                    ? "Assistant is speaking..."
                    : "Tap the microphone to speak"}
              </p>
            </div>
          </div>
        </div>

        <div className="conversation-container">
          {conversation.length > 0 && (
            <div className="conversation-scrollbox">
              {[...conversation].reverse().map((message, index) => (
                <div key={index} className={`message-bubble ${message.role === 'user' ? 'user-bubble' : 'assistant-bubble'}`}>
                  <p className="message-sender">
                    {message.role === 'user' ? 'You' : 'Marc Fisher Assistant'}
                  </p>
                  <p className="message-text">{message.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="app-footer">
        <div className="footer-status">
          <div className={`status-dot ${isConnected ? 'status-connected' : 'status-disconnected'}`}></div>
          <span>
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>
        <p>© {new Date().getFullYear()} Marc Fisher AI Assistant</p>
      </footer>
    </div>
  );
}

export default App;
